import { graphql } from "babel-plugin-relay/macro";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import * as React from "react";
import { useFragment, useMutation } from "react-relay";
import * as Yup from "yup";
import { InstructorsMultiSelect } from "./InstructorsMultiSelect";
import { EditNodeForm_EditNodeCoreMutation } from "../../../../../__generated__/EditNodeForm_EditNodeCoreMutation.graphql";
import { EditNodeForm_TreeNodeFragment$key } from "../../../../../__generated__/EditNodeForm_TreeNodeFragment.graphql";
import { ADMIN_CONFIG_IMPL } from "../../../../../config";
import { countLettersAndSpaces } from "../../../../functions/textCount";
import { SHORT_DESCRIPTION } from "../../../../i18n/graph/shortDescription-label";
import {
	removeEditedFormFromEditedFormsArray,
	addEditedFormToEditedFormsArray,
} from "../../../../redux/slices/CoreSlice";
import { useTypedDispatch } from "../../../../redux/Store";
import { DefaultTextEditorComponent } from "../../../core/form/DefaultTextEditorComponent";
import { DefaultTextFieldComponent } from "../../../core/form/DefaultTextInput";
import { ValidatedField } from "../../../core/form/ValidatedField";
import { FileSelectionField } from "../../../files/FileSelectionField";
import { ImageFileSelectionField } from "../../../files/ImageFileSelectionField";

const TREE_NODE_FRAGMENT = graphql`
	fragment EditNodeForm_TreeNodeFragment on TreeNode {
		id
		structureDefinition {
			title
			definitionType
		}
		description
		shortDescription
		instructors {
			superId
		}
		imageId
	}
`;

const EDIT_NODE_CORE_MUTATION = graphql`
	mutation EditNodeForm_EditNodeCoreMutation($input: EditNodeCoreInput!) {
		Admin {
			Tree {
				editNodeCore(input: $input) {
					editedNode {
						id
						...EditNodeForm_TreeNodeFragment
						...Node_TreeNodeFragment
					}
				}
			}
		}
	}
`;

const SHORT_DESCRIPTION_MAX_LENGTH = 200;
const TITLE_MAX_LENGTH = 80;
const DESCRIPTION_MAX_LENGTH = 200;

interface OwnProps {
	treeNodeFragmentRef: EditNodeForm_TreeNodeFragment$key;
}

interface FormState {
	title: string;
	description?: string;
	shortDescription?: string;
	instructors: any;
	imageId?: string;
}

export const EditNodeForm = ({ treeNodeFragmentRef }: OwnProps) => {
	const node = useFragment<EditNodeForm_TreeNodeFragment$key>(
		TREE_NODE_FRAGMENT,
		treeNodeFragmentRef,
	);

	const isRoot = node?.structureDefinition.definitionType == "root";
	const dispatch = useTypedDispatch();
	const [editNodeCore, isEditingNodeCore] =
		useMutation<EditNodeForm_EditNodeCoreMutation>(EDIT_NODE_CORE_MUTATION);
	const isWissenwirktAdmin =
		ADMIN_CONFIG_IMPL.brandId == process.env.REACT_APP_APP_WISSENWIRKT_BRAND_ID;

	const formId = "EditNodeForm";

	const validationSchema = (isWissenwirktAdmin: boolean) =>
		Yup.object().shape({
			title: Yup.string()
				.max(TITLE_MAX_LENGTH, `Überschreitet ${TITLE_MAX_LENGTH} Zeichen`)
				.required("Titel wird benötigt"),

			description: isWissenwirktAdmin
				? Yup.string().test(
						"desc-visible-max",
						`Überschreitet ${DESCRIPTION_MAX_LENGTH} Zeichen`,
						(value) => {
							const len = countLettersAndSpaces(value || "");
							return len <= DESCRIPTION_MAX_LENGTH;
						},
					)
				: Yup.string(),

			shortDescription: Yup.string().test(
				"short-visible-max",
				`Überschreitet ${SHORT_DESCRIPTION_MAX_LENGTH} Zeichen`,
				(value) => {
					const len = countLettersAndSpaces(value || "");
					return len <= SHORT_DESCRIPTION_MAX_LENGTH;
				},
			),
		});

	const formik = useFormik<FormState>({
		enableReinitialize: true,
		initialValues: {
			title: node.structureDefinition.title,
			description: node.description || undefined,
			shortDescription: node.shortDescription || undefined,
			imageId: node.imageId || undefined,
			instructors: node.instructors.map((i) => i.superId) || null,
		},
		validationSchema: validationSchema(isWissenwirktAdmin),

		onSubmit: (values, { setSubmitting }) => {
			editNodeCore({
				variables: {
					input: {
						nodeId: node.id,
						title: values.title,
						description: values.description,
						shortDescription: values.shortDescription,
						imageId: values.imageId,
						instructorIds: values.instructors || [],
					},
				},
				onCompleted: () => {
					formik.setTouched({});
					setSubmitting(false);
					dispatch(removeEditedFormFromEditedFormsArray({ form: formId }));
				},
			});
		},
	});
	const descCount = countLettersAndSpaces(formik.values.description);
	const shortCount = countLettersAndSpaces(formik.values.shortDescription);
	return (
		<Card className="mb-2">
			<h2>Ordner-Einstellungen</h2>
			<form onSubmit={formik.handleSubmit} className="p-fluid">
				<ValidatedField<FormState, string>
					name={"title"}
					label={"Titel"}
					component={DefaultTextFieldComponent}
					helpText={`Maximal ${TITLE_MAX_LENGTH} Zeichen (${
						formik.values.title?.length || 0
					} / ${TITLE_MAX_LENGTH})`}
					onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
					formikConfig={formik}
				/>
				{isRoot && (
					<ValidatedField<FormState, string>
						name={"description"}
						label={"Beschreibung"}
						component={DefaultTextEditorComponent}
						helpText={
							isWissenwirktAdmin
								? `Maximal ${DESCRIPTION_MAX_LENGTH} Zeichen (${
										descCount || 0
									} / ${DESCRIPTION_MAX_LENGTH})`
								: ""
						}
						onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
						formikConfig={formik}
						showListButtons={true}
					/>
				)}
				<ValidatedField<FormState, string[]>
					name={"instructors"}
					label={"Expert:innen"}
					helpText={"Wählen Sie die für das Modul verantwortlichen Lehrenden."}
					onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
					component={InstructorsMultiSelect}
					formikConfig={formik}
				/>
				<ValidatedField<FormState, string>
					name={"shortDescription"}
					label={SHORT_DESCRIPTION["short_description"]}
					helpText={`Maximal ${SHORT_DESCRIPTION_MAX_LENGTH} Zeichen (${
						shortCount || 0
					} / ${SHORT_DESCRIPTION_MAX_LENGTH})`}
					component={DefaultTextEditorComponent}
					onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
					formikConfig={formik}
				/>

				<ValidatedField<FormState, string>
					name={"imageId"}
					label={"Bild"}
					onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
					component={ImageFileSelectionField}
					formikConfig={formik}
				/>

				<Button
					disabled={Object.entries(formik.touched).length === 0 || isEditingNodeCore}
					type="submit"
					label="Speichern"
					className="p-mt-2"
				/>
			</form>
		</Card>
	);
};
